import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaArrowRight, FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaPalette, FaShieldAlt, FaTimesCircle } from "react-icons/fa";
import TooltipButton from "../../Tools/TooltipButton.tsx";
import { ColumnAccessRequest, ColumnRestrictionGroup } from "../_core/_model.ts";
import { GetColumnRestrictions, GetUserAssignedGroup } from "../_core/_request.ts";

interface ColumnAccessRequestCardProps {
    req: ColumnAccessRequest;
    allAvailableColumns: { id: string, name: string }[];
    getUserName: (id: number) => string;
    onProcess: (req: ColumnAccessRequest, status: "Approved" | "Rejected", payloadOptions: any) => void;
    isProcessing: boolean;
}

export default function ColumnAccessRequestCard({
    req,
    allAvailableColumns,
    getUserName,
    onProcess,
    isProcessing
}: ColumnAccessRequestCardProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [rejectReason, setRejectReason] = useState("");

    const [isLoadingData, setIsLoadingData] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [localGroups, setLocalGroups] = useState<ColumnRestrictionGroup[]>([]);
    const [localCurrentGroup, setLocalCurrentGroup] = useState<ColumnRestrictionGroup | null>(null);

    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingAction, setPendingAction] = useState<"Approved" | "Rejected" | null>(null);
    const [selectedGroupId, setSelectedGroupId] = useState<number>(0);

    useEffect(() => {
        if (isExpanded && !hasLoaded) {
            const fetchDetails = async () => {
                setIsLoadingData(true);
                try {
                    const [groupsRes, userGroupRes] = await Promise.all([
                        GetColumnRestrictions({ columnRestrictionId: 0, columns: null, users: null, name: null }),
                        GetUserAssignedGroup(req.userId)
                    ]);
                    setLocalGroups(groupsRes.data);
                    setLocalCurrentGroup(userGroupRes.data || null);
                } catch (e) {

                } finally {
                    setIsLoadingData(false);
                    setHasLoaded(true);
                }
            };
            fetchDetails();
        }
    }, [isExpanded, hasLoaded, req.userId, req.columnName]);

    const currentColumns = localCurrentGroup ? localCurrentGroup.restrictedColumns : [];
    const targetColumns = currentColumns != null ? Array.from(new Set([...currentColumns, req.columnName])) : [req.columnName];

    const possibleGroups = localGroups.filter(g =>
        targetColumns.every(tc => g.restrictedColumns.includes(tc))
    );

    useEffect(() => {
        if (possibleGroups.length > 0 && selectedGroupId === 0) {
            setSelectedGroupId(possibleGroups[0].columnRestrictionId);
        }
    }, [possibleGroups, selectedGroupId]);

    const selectedGroupObj = localGroups.find(g => g.columnRestrictionId === selectedGroupId);

    const handlePrepareProcess = (actionType: "Approved" | "Rejected") => {
        setPendingAction(actionType);
        setShowConfirm(true);
    };

    const handleConfirmProcess = () => {
        setShowConfirm(false);

        const options: any = { userId: req.userId };

        if (pendingAction === "Rejected") {
            options.rejectReason = rejectReason;
        } else if (pendingAction === "Approved") {
            options.assignGroupId = selectedGroupId;
            options.rejectReason = rejectReason;
        }

        onProcess(req, pendingAction!, options);
    };

    const renderColumns = (cols: string[], type: "success" | "primary") => {
        if (cols == null || cols.length === 0) return <span className="text-muted small fst-italic">None Assigned</span>;
        return (
            <div className="d-flex flex-wrap gap-1 mt-1">
                {cols.map(c => <Badge key={c} bg={type} className="fw-normal">{c}</Badge>)}
            </div>
        );
    };

    return (
        <div className={`card shadow-sm border rounded-3 overflow-hidden bg-theme-card mb-3 ${isExpanded ? 'border-primary' : 'border-secondary-subtle'}`}>
            <div
                className="card-header bg-transparent py-3 d-flex justify-content-between align-items-center cursor-pointer border-bottom-0"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="d-flex align-items-center gap-3">
                    <div className="bg-primary bg-opacity-10 text-primary p-2 rounded-circle d-flex shadow-sm">
                        <FaShieldAlt size={20} />
                    </div>
                    <div>
                        <h6 className="mb-0 fw-bold">{getUserName(req.userId)} <span className="fw-normal text-muted">requested access to</span> {req.columnName}</h6>
                        <span className="text-muted small">Requested on {new Date(req.requestDate).toLocaleDateString()}</span>
                    </div>
                </div>
                <Button variant={isExpanded ? "outline-secondary" : "primary"} size="sm" className="rounded-pill px-3 shadow-sm">
                    {isExpanded ? "Close" : "Review Request"}
                </Button>
            </div>

            <AnimatePresence>
                {isExpanded && (
                    <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
                        <div className="card-body bg-light border-top">
                            {isLoadingData ? (
                                <div className="d-flex flex-column justify-content-center align-items-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <span className="mt-3 text-muted small fw-medium">Loading user permissions and resolving groups...</span>
                                </div>
                            ) : (
                                <>
                                    <div className="bg-white p-3 rounded-3 border shadow-sm mb-3">
                                        <span className="small text-muted text-uppercase fw-bold"><FaInfoCircle className="me-1" /> Reason Provided</span>
                                        <p className="mb-0 mt-1 fst-italic text-dark">"{req.reason}"</p>
                                    </div>

                                    <div className="bg-white p-4 rounded-3 border shadow-sm mb-3">
                                        <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                            <FaShieldAlt className="text-primary" /> Step 1: Access Resolution
                                        </h6>
                                        <div className="row g-3">
                                            <div className="col-md-5">
                                                <div className="p-3 bg-light rounded border h-100">
                                                    <span className="text-muted small fw-bold text-uppercase">Current State</span>
                                                    <div className="mt-2 text-dark small"><strong>Assigned Group:</strong> {localCurrentGroup ? localCurrentGroup.name : <span className="fst-italic text-muted">None</span>}</div>
                                                    <div className="mt-2 text-dark small"><strong>Currently Accessible:</strong></div>
                                                    {renderColumns(currentColumns, "primary")}
                                                </div>
                                            </div>
                                            <div className="col-md-2 d-flex justify-content-center align-items-center flex-column">
                                                <Badge bg="success" className="mb-2 shadow-sm">+{req.columnName}</Badge>
                                                <FaArrowRight size={24} className="text-muted opacity-50" />
                                            </div>
                                            <div className="col-md-5">
                                                <div className="p-3 bg-primary bg-opacity-10 border border-primary-subtle rounded h-100">
                                                    <span className="text-primary small fw-bold text-uppercase">Target State (Approved)</span>
                                                    <div className="mt-2 text-dark small"><strong>Required Columns:</strong></div>
                                                    {renderColumns(targetColumns, "primary")}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white p-4 rounded-3 border shadow-sm mb-4">
                                        <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                            <FaCheckCircle className="text-success" /> Step 2: Group Assignment
                                        </h6>

                                        {possibleGroups.length > 0 ? (
                                            <div className="alert alert-success d-flex flex-column gap-2 mb-0 shadow-sm border-success-subtle">
                                                <div><strong>Eligible Groups Found</strong></div>
                                                <div className="small">Please select the group to assign this user to upon approval:</div>
                                                <Form.Select
                                                    size="sm"
                                                    value={selectedGroupId}
                                                    onChange={(e) => setSelectedGroupId(Number(e.target.value))}
                                                    className="shadow-none w-50 border-success-subtle"
                                                >
                                                    {possibleGroups.map(g => (
                                                        <option key={g.columnRestrictionId} value={g.columnRestrictionId}>
                                                            {g.name}
                                                        </option>
                                                    ))}
                                                </Form.Select>

                                                {selectedGroupObj && (
                                                    <div className="mt-2 p-2 bg-white rounded border border-success-subtle small">
                                                        <div className="text-muted fw-bold mb-1"><FaPalette className="me-1" /> Columns contained in this group:</div>
                                                        <div className="text-dark">{selectedGroupObj.restrictedColumns.join(", ")}</div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="alert alert-warning d-flex flex-column gap-2 mb-0 shadow-sm border-warning-subtle">
                                                <div><FaExclamationTriangle className="me-2" /><strong>No Eligible Group Found</strong></div>
                                                <div className="small">
                                                    No existing group contains all the required columns: <strong>{targetColumns.join(", ")}</strong>.
                                                    To approve this request, you must first create a matching group in the <em>Restriction Groups</em> tab, then return here.
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="d-flex gap-3 align-items-center pt-2">
                                        <div className="flex-grow-1">
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                placeholder="Provide an optional comment or rejection reason..."
                                                value={rejectReason}
                                                onChange={(e) => setRejectReason(e.target.value)}
                                                className="shadow-none border-secondary-subtle"
                                            />
                                        </div>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handlePrepareProcess("Rejected")}
                                            disabled={isProcessing}
                                            className="d-flex align-items-center gap-2 px-3 bg-white"
                                        >
                                            {isProcessing && pendingAction === "Rejected" ? <Spinner size="sm" animation="border" /> : <FaTimesCircle />} Reject
                                        </Button>
                                        <TooltipButton title={possibleGroups.length === 0 ? "You must create an eligible group first" : ""}>
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={() => handlePrepareProcess("Approved")}
                                                disabled={isProcessing || possibleGroups.length === 0}
                                                className="d-flex align-items-center gap-2 px-4 shadow-sm"
                                            >
                                                {isProcessing && pendingAction === "Approved" ? <Spinner size="sm" animation="border" /> : <FaCheckCircle />} Approve Request
                                            </Button>
                                        </TooltipButton>
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered backdrop="static" contentClassName="bg-theme-card">
                <Modal.Header closeButton className="border-bottom-0 pb-0">
                    <Modal.Title className="fw-bold d-flex align-items-center gap-2">
                        {pendingAction === "Approved" ? <><FaCheckCircle className="text-success" /> Confirm Approval</> : <><FaTimesCircle className="text-danger" /> Confirm Rejection</>}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-3">
                    <p>Are you sure you want to <strong>{pendingAction == "Approved" ? "approve" : "reject"}</strong> access to <strong>{req.columnName}</strong> for <strong>{getUserName(req.userId)}</strong>?</p>

                    {pendingAction === "Approved" && possibleGroups.length > 0 && (
                        <div className="bg-light p-3 rounded border small">
                            <div className="text-muted fw-bold mb-2">Execution Plan:</div>
                            <ul className="mb-0 text-dark">
                                {localCurrentGroup && <li>Remove user from current group: <strong>{localCurrentGroup.name}</strong></li>}
                                <li>Assign user to new group: <strong>{selectedGroupObj?.name}</strong></li>
                            </ul>
                        </div>
                    )}

                    {pendingAction === "Rejected" && (
                        <div className="bg-light p-3 rounded border small">
                            <strong>Reason:</strong> {rejectReason || <span className="text-muted fst-italic">No reason provided</span>}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-top-0 pt-0">
                    <Button variant="outline-secondary" className="bg-white" onClick={() => setShowConfirm(false)}>Cancel</Button>
                    <Button variant={pendingAction === "Approved" ? "success" : "danger"} onClick={handleConfirmProcess} className="px-4 shadow-sm">
                        Confirm & {pendingAction == "Approved" ? "Approve" : "Reject"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}